import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TransferCommonParameters, TransferLinkParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions } from '@app/client/uploader/store/actions';
import { Domain } from '@app/shared/models/domain';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Identity } from '@app/shared/models/identity';
import { Transfer } from '@app/shared/models/transfer';
import { AccessTrackingType, LinkDelivery } from '@app/shared/models/transferConfiguration';
import { Store } from '@ngrx/store';
import { UploaderStatus } from '@smash-sdk/uploader';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';

@Component({
  selector: 'app-link-form',
  templateUrl: './link-form.component.html',
  styleUrls: ['./link-form.component.scss']
})
export class LinkFormComponent implements OnInit {

  @Input() identity: Identity;
  @Input() domain: Domain;
  @Input() transferSettings: TransferSettings;

  @Output() linkFormSubmitted: EventEmitter<{
    event: Event,
    params: {
      title: string,
      customUrl: string,
      delivery: LinkDelivery,
    }
  }> = new EventEmitter();

  uploadError$: Observable<any>;
  transfer$: Observable<Transfer>;
  commonParameters$: Observable<TransferCommonParameters>;
  linkParameters$: Observable<TransferLinkParameters>;
  uploadStatus$: Observable<UploaderStatus>;

  linkForm: FormGroup;
  submitted = false;
  prepareState: any;
  errorMessageLinkAlreadyExists = Constant.errorStatus.uploader.linkAlreadyExists;
  showMessageDisabled = false;
  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromUploader.State>,
    private readonly router: Router
  ) {
    this.uploadError$ = this.store$.select(fromUploader.getUploadError);
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.commonParameters$ = this.store$.select(fromUploader.getTransferCommonParameters);
    this.linkParameters$ = this.store$.select(fromUploader.getTransferLinkParameters);
    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);

    this.linkForm = new FormGroup({
      title: new FormControl(''),
      sender: new FormGroup({
        email: new FormControl('', [Validators.required, Validators.pattern(Constant.regexpEmail)]),
      }),
      customUrl: new FormControl('', Validators.pattern(Constant.regexpCustomUrl)),
      accessTracking: new FormControl(this.transferSettings?.accessTracking?.default === Constant.accessTracking.Email ? true : false),
    });
  }

  ngOnInit() {
    this.loadFormData();
    this.handleFormChanges();
  }

  get f() { return this.linkForm.controls; }
  get senderEmailControls() { return this.linkForm.get('sender.email'); }

  onSubmit($event: Event) {
    this.submitted = true;
    if (this.linkForm.valid) {
      const { title, sender, customUrl, accessTracking } = this.linkForm.value;
      const buildedLinkFormValue = {
        title,
        customUrl,
        delivery: {
          type: 'Link' as const,
          sender,
        },
        accessTracking: accessTracking ? 'Email' : 'None',
      };
      this.store$.dispatch(PrepareActions.SetTransferAccessTracking({ accessTracking: accessTracking ? 'Email' : 'None' }));
      this.linkFormSubmitted.emit({ event: $event, params: buildedLinkFormValue });
    }
  }

  goBack() {
    this.router.navigateByUrl('');
  }

  onTitleChange(value) {
    const customUrl = this._slugify(value);
    // tslint:disable-next-line: no-string-literal
    this.linkForm.controls['customUrl'].setValue(customUrl, { emitEvent: false });
    // Publish it explicitly, because the STORE is what ships the transfer: onSubmit does emit a fresh
    // customUrl in its payload, but the parent's handleFormSubmitted reads only `$event.event` from it and
    // the upload effect builds CreateTransfer from store state.
    //
    // Until the ControlValueAccessor contract was fixed, this dispatch happened by accident: the silent
    // setValue above notified the form anyway (writeValue delegated to a `value` setter that called
    // onChange), producing a second valueChanges that carried the fresh slug into the store. Removing that
    // echo removed the only path from the slug to the store, leaving just the FIRST valueChanges of each
    // keystroke -- which fires before this method runs and therefore carries the PREVIOUS slug. Type
    // "report" and the store held "repor".
    //
    // Dispatching here rather than dropping `{ emitEvent: false }`: letting the form emit would re-dispatch
    // all four link fields on every keystroke, and the silence is deliberate.
    this.store$.dispatch(PrepareActions.SetLinkCustomUrl({ customUrl }));
  }

  _slugify(str: string) {
    // '_' is deliberately NOT in this table (kept in the input as-is): unlike these accented/special
    // characters, an underscore is already a valid \w character, so it survives the
    // "remove all non-word characters" step below untouched -- no substitution needed, and
    // substituting it to '-' would corrupt a title like "titre_v2" into "titre-v2".
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/,:;';
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz-----';
    const p = new RegExp(a.split('').join('|'), 'g');

    return str ? str.toString() // .toLowerCase()
      // Strip diacritics uniformly for BOTH cases (É À Ç Î Ô as well as é à ç…). The `a`/`b` table
      // below only listed lowercase accents, so uppercase-accented titles were dropped by the ASCII
      // `\w` filter further down ("Été" became "te"). NFD decomposes a precomposed letter into its
      // base + a combining mark, which we remove here; the table still handles the non-decomposable
      // letters it covers (æ ø ß ł đ œ…).
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '')  // Trim - from end of text
      : null;
  }

  inputClick(event) {
    if (event.target.offsetParent.className.indexOf('disabled') > -1) {
      this.showMessageDisabled = true;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  loadFormData() {
    this.commonParameters$.pipe(first()).subscribe((params) => {
      this.linkForm.patchValue({ ...params });
    });
    this.linkParameters$.pipe(first()).subscribe((params) => {
      let accessTracking = this.transferSettings?.accessTracking?.default === Constant.accessTracking.Email ? true : false;
      if (params.accessTracking) {
        accessTracking = params.accessTracking === Constant.accessTracking.Email ? true : false;
      }
      this.linkForm.patchValue({ ...params, ...params.delivery, accessTracking });
    });
  }

  handleFormChanges() {
    this.subscriptions.push(
      this.linkForm.valueChanges.subscribe(changes => {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: changes.sender }));
        this.store$.dispatch(PrepareActions.SetLinkTitle({ title: changes.title }));
        this.store$.dispatch(PrepareActions.SetLinkCustomUrl({ customUrl: changes.customUrl }));
        this.store$.dispatch(PrepareActions.SetTransferAccessTracking({ accessTracking: changes.accessTracking ? 'Email' : 'None' }));
      })
    );
  }

  get isAccessTrackingEnabled(): boolean {
    return this.transferSettings?.accessTracking?.active;
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

}
